/**
 * ModeThemeContext (web) — Annie Mode app-global theming for apps/web.
 *
 * Annie Mode Phase 8 (PR #152). Mirrors the mobile pattern in
 * apps/mobile/src/contexts/ModeThemeContext.tsx but uses standard CSS
 * variables on the document root (no NativeWind machinery).
 *
 * Split-provider pattern (same rationale as mobile):
 *  - ModeThemeProvider — owns mode state, hydrates from localStorage,
 *    writes --brand-* CSS variables on the document root.
 *  - ModeThemeBinder — reads useAuth().user.activeMode and pushes into the
 *    provider. Lives INSIDE AuthProvider so the provider can wrap surfaces
 *    outside AuthProvider if needed.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  type ActiveMode,
  type ModePalette,
  getModePalette,
} from '@trading-app/shared';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'trading-app:lastKnownActiveMode';

interface ModeThemeContextValue {
  mode: ActiveMode;
  palette: ModePalette;
  setActiveMode: (mode: ActiveMode) => void;
}

const ModeThemeContext = createContext<ModeThemeContextValue | null>(null);

function isValidMode(value: string | null): value is ActiveMode {
  return value === 'ERIC' || value === 'ANNIE';
}

function readPersistedMode(): ActiveMode {
  try {
    const v = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    return isValidMode(v) ? v : 'ERIC';
  } catch {
    return 'ERIC';
  }
}

export function ModeThemeProvider({ children }: { children: React.ReactNode }) {
  // Hydrate synchronously on first render so we don't flash Eric's defaults.
  const [mode, setMode] = useState<ActiveMode>(() => readPersistedMode());

  // Apply CSS variables on every mode change. Document-root style keeps the
  // entire app inside the variable scope (web has a single root unlike RN).
  useEffect(() => {
    const palette = getModePalette(mode);
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', palette.brandPrimaryRgb);
    root.style.setProperty('--brand-secondary', palette.brandSecondaryRgb);
    root.style.setProperty('--brand-accent', palette.brandAccentRgb);
  }, [mode]);

  const setActiveMode = useCallback((next: ActiveMode) => {
    setMode((current) => {
      if (current === next) return current;
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // localStorage may be disabled (private browsing). Mode still flips
        // in memory; next cold start defaults to ERIC. Acceptable.
      }
      return next;
    });
  }, []);

  const palette = useMemo(() => getModePalette(mode), [mode]);
  const value = useMemo<ModeThemeContextValue>(
    () => ({ mode, palette, setActiveMode }),
    [mode, palette, setActiveMode],
  );

  return <ModeThemeContext.Provider value={value}>{children}</ModeThemeContext.Provider>;
}

/** Lives inside AuthProvider; renders nothing; binds user.activeMode into the provider. */
export function ModeThemeBinder() {
  const { user } = useAuth();
  const ctx = useContext(ModeThemeContext);
  useEffect(() => {
    if (!ctx) return;
    if (user?.activeMode === 'ERIC' || user?.activeMode === 'ANNIE') {
      ctx.setActiveMode(user.activeMode);
    }
  }, [ctx, user?.activeMode]);
  return null;
}

export function useModeTheme(): ModeThemeContextValue {
  const ctx = useContext(ModeThemeContext);
  if (!ctx) throw new Error('useModeTheme must be used inside ModeThemeProvider');
  return ctx;
}
